
import { getUserRole } from "../../services/apiService";
import { User, Role, stringToRole } from "../types/User";
import { RoleAccess } from "./RoleAccess";


export const hasAccess = (user: User | null, component: string): boolean => {
    if (!user) return false; // if no user is provided, deny access
    
    const roleHierarchy = [Role.USER, Role.BRONZE, Role.SILVER, Role.GOLD, Role.ADMIN];
    const userRoleIndex = roleHierarchy.indexOf(user.role);
    
    return roleHierarchy
      .slice(0, userRoleIndex + 1)
      .some(role => RoleAccess[role].includes(component));
  }
  

  export const hasPositiveEVAccess = (user: User | null): boolean =>{
   return hasAccess(user,"PositiveEV") 
  }



  export const roleHasAccess = (role: Role | null, component: string): boolean => {
    if(!role) return false;
    const roleHierarchy = [Role.USER, Role.BRONZE, Role.SILVER, Role.GOLD, Role.ADMIN];
    const userRoleIndex = roleHierarchy.indexOf(role);
    
    return roleHierarchy
      .slice(0, userRoleIndex + 1)
      .some(role => RoleAccess[role].includes(component));
  }


  export const roleHasPositiveEVAccess = (role: Role | null): boolean =>{
    return roleHasAccess(role,"PositiveEV") 
   }

   export const currentUserHasEVAccess = (): boolean =>{
    const userRoleString = getUserRole() ?? "";
    const userRole = stringToRole(userRoleString);
    const pop = roleHasAccess(userRole,"PositiveEV");
    return pop;

   }

   export const currentUserHasArbAccess = (): boolean =>{
    const userRoleString = getUserRole() ?? "";
    const userRole = stringToRole(userRoleString);
    return roleHasAccess(userRole,"Arbitrage");
   }

   /**
    * Silver+ gate for the AI Parlay Builder. Bronze does not include
    * Parlay Builder per RoleAccess.ts — the previous `/parlay-builder`
    * route incorrectly used currentUserHasEVAccess (Bronze gate), which
    * gave $29.99 subscribers the same access as $49.99 Silver
    * subscribers. New code must use this helper.
    */
   export const currentUserHasParlayBuilderAccess = (): boolean => {
    const userRoleString = getUserRole() ?? "";
    const userRole = stringToRole(userRoleString);
    return roleHasAccess(userRole, "ParlayBuilder");
   }

   /**
    * Admin-only gate for the Kalshi BYOK trading surface (settings
    * section + inline "Place bet" CTAs). The backend additionally enforces
    * this at the security-config layer and behind the KALSHI_TRADING_ENABLED
    * feature flag.
    */
   export const currentUserHasKalshiTradingAccess = (): boolean => {
    const userRoleString = getUserRole() ?? "";
    const userRole = stringToRole(userRoleString);
    return roleHasAccess(userRole, "KalshiTrading");
   }

   /**
    * Visibility-only gate for Kalshi (and future Polymarket) post-fee odds
    * chips and the listed/net toggle context. Bronze+ users get this; the
    * actual order-placement CTA is still gated by
    * {@link currentUserHasKalshiTradingAccess}. The chip is computed
    * client-side from the listed price already on the row, so this gate
    * never triggers a backend Kalshi call.
    */
   export const currentUserHasKalshiOddsViewAccess = (): boolean => {
    const userRoleString = getUserRole() ?? "";
    const userRole = stringToRole(userRoleString);
    return roleHasAccess(userRole, "KalshiOddsView");
   }

   /**
    * Admin-only gate for the BookieBurglar Bet Agent (browser-extension
    * driven sportsbook screen control). Gates:
    *   - "Send to agent" CTAs in +EV / arb / parlay surfaces
    *   - the sticky agent status drawer
    *   - the bridge handshake (we don't even probe for the extension
    *     when the user isn't admin)
    * The extension itself is still safe to install — it ignores
    * intents from non-admin pages because nothing emits any — but
    * we keep the React side dark to avoid leaking the surface.
    */
   export const currentUserHasBetAgentAccess = (): boolean => {
    const userRoleString = getUserRole() ?? "";
    const userRole = stringToRole(userRoleString);
    return roleHasAccess(userRole, "BetAgent");
   }

   export const currentUserHasPredictionTradersAccess = (): boolean => {
    const userRoleString = getUserRole() ?? "";
    const userRole = stringToRole(userRoleString);
    return roleHasAccess(userRole, "PredictionTraders");
   }

   /**
    * Admin-only gate for the Sharp Money scanner (exchange-liquidity smart-money
    * tool). Non-admins still reach the public /sharp-money route but see the
    * upsell shell instead of the live scanner; backend candidates are ADMIN-only.
    */
   export const currentUserHasSharpMoneyAccess = (): boolean => {
    const userRoleString = getUserRole() ?? "";
    const userRole = stringToRole(userRoleString);
    return roleHasAccess(userRole, "SharpMoney");
   }

   /**
    * True for a signed-in account on the free role. Decides which free
    * surface to show, never what the API returns: the server gates the data.
    */
   export const currentUserIsFreeTier = (): boolean =>
    stringToRole(getUserRole() ?? "") === Role.USER;

   /**
    * True only for the ADMIN role. Used to gate internal-only UI affordances
    * (e.g. the prediction-traders provenance/tier markers) that paying users
    * shouldn't see — never for security-critical access decisions, which go
    * through roleHasAccess against a named component.
    */
   export const currentUserIsAdmin = (): boolean =>
    stringToRole(getUserRole() ?? "") === Role.ADMIN;

